import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';
import { Testimonial, PersonalInfo } from '../types';

interface TestimonialsCarouselProps {
  testimonials: Testimonial[];
  personalInfo: PersonalInfo;
}

const TestimonialsCarousel = ({ testimonials, personalInfo }: TestimonialsCarouselProps) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [direction, setDirection] = useState(0);

  const slideVariants = {
    enter: (direction: number) => ({
      x: direction > 0 ? 50 : -50,
      opacity: 0
    }),
    center: {
      zIndex: 1,
      x: 0,
      opacity: 1
    },
    exit: (direction: number) => ({
      zIndex: 0,
      x: direction < 0 ? 50 : -50,
      opacity: 0
    })
  };

  const goToNext = () => {
    setDirection(1);
    setCurrentIndex((prev) => (prev + 1) % testimonials.length);
  };

  const goToPrevious = () => {
    setDirection(-1);
    setCurrentIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  const goToSlide = (index: number) => {
    setDirection(index > currentIndex ? 1 : -1);
    setCurrentIndex(index);
  };

  if (testimonials.length === 0) return null;

  return (
    <section className="py-24 relative bg-transparent overflow-hidden" id="testimonials">
      <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-16">
        {/* Section Header */}
        <div className="text-center mb-16">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="inline-block px-4 py-1.5 rounded-full bg-teal-900/5 text-[#0C2C39]/60 text-[10px] font-black uppercase tracking-[0.3em] mb-6"
          >
            {personalInfo.testimonialsBadge || "Verification of Impact"}
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="font-roboto text-4xl sm:text-5xl font-black text-[#0C2C39] mb-6 tracking-tight"
          >
            {personalInfo.testimonialsTitle || "Trusted by Industry Leaders"}
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-lg text-[#0C2C39]/60 font-medium max-w-xl mx-auto"
          >
            {personalInfo.testimonialsSubtitle || ""}
          </motion.p>
        </div>

        {/* Carousel Container */}
        <div className="relative max-w-4xl mx-auto">
          <div className="relative h-[480px] md:h-[420px]">
            <AnimatePresence initial={false} custom={direction}>
              <motion.div
                key={currentIndex}
                custom={direction}
                variants={slideVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{
                  x: { type: "spring", stiffness: 300, damping: 30 },
                  opacity: { duration: 0.2 }
                }}
                className="absolute inset-0"
              >
                <div className="bg-white h-full rounded-[3rem] p-10 md:p-14 flex flex-col justify-center items-center text-center border border-teal-900/5 shadow-2xl relative overflow-hidden group">
                  {/* Decorative Quote Mark */}
                  <div className="absolute top-10 right-10 opacity-[0.03] text-[20rem] font-black pointer-events-none select-none text-[#0C2C39]">
                    "
                  </div>

                  {/* Quote Icon - Teal Theme */}
                  <div className="mb-8 p-4 rounded-2xl bg-teal-900/5 text-teal-600">
                    <Icon name="Quote" size={40} />
                  </div>

                  {/* Testimonial Content */}
                  <blockquote className="text-xl md:text-2xl text-[#1A202C] mb-10 leading-relaxed font-black tracking-tight max-w-2xl">
                    "{testimonials[currentIndex].content}"
                  </blockquote>

                  {/* Client Info */}
                  <div className="flex flex-col items-center">
                    <div className="w-16 h-16 rounded-2xl overflow-hidden border border-teal-900/10 mb-5 bg-[#FFF9EB] p-1 shadow-inner">
                      <Image
                        src={testimonials[currentIndex].avatar}
                        alt={testimonials[currentIndex].avatarAlt}
                        className="w-full h-full object-cover rounded-xl"
                      />
                    </div>
                    <div>
                      <div className="text-lg font-roboto font-black text-[#0C2C39] tracking-tight">
                        {testimonials[currentIndex].name}
                      </div>
                      <div className="text-[10px] font-black uppercase tracking-widest text-teal-600 mt-1">
                        {testimonials[currentIndex].position} <span className="text-slate-400">@</span> {testimonials[currentIndex].company}
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Controls - Minimal Executive Style */}
          <div className="mt-12 flex items-center justify-between px-6">
            <div className="flex space-x-2.5">
              {testimonials.map((_, index) => (
                <button
                  key={index}
                  onClick={() => goToSlide(index)}
                  className={`h-1.5 transition-all duration-500 rounded-full ${index === currentIndex ? 'w-10 bg-[#0C2C39]' : 'w-4 bg-teal-900/10'
                    }`}
                  aria-label={`Go to slide ${index + 1}`}
                />
              ))}
            </div>

            <div className="flex space-x-4">
              <button
                onClick={goToPrevious}
                className="p-4 rounded-2xl bg-white border border-teal-900/5 text-[#0C2C39] hover:bg-[#0C2C39] hover:text-white transition-all duration-300 shadow-sm"
                aria-label="Previous slide"
              >
                <Icon name="ChevronLeft" size={20} />
              </button>
              <button
                onClick={goToNext}
                className="p-4 rounded-2xl bg-white border border-teal-900/5 text-[#0C2C39] hover:bg-[#0C2C39] hover:text-white transition-all duration-300 shadow-sm"
                aria-label="Next slide"
              >
                <Icon name="ChevronRight" size={20} />
              </button>
            </div>
          </div>
        </div>

        {/* Executive Impact Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mt-32 pt-16 border-t border-teal-900/5">
          {[
            { label: 'Architectural Success', val: '100%', icon: 'ShieldCheck' },
            { label: 'Client Trust Index', val: '4.9/5.0', icon: 'Star' },
            { label: 'Partner Retention', val: '92%', icon: 'Users' },
            { label: 'Engineering Velocity', val: 'Superior', icon: 'Zap' }
          ].map((stat, i) => (
            <div key={i} className="flex flex-col items-center text-center space-y-3">
              <div className="text-3xl font-black text-[#0C2C39] tracking-tighter">{stat.val}</div>
              <div className="text-[9px] font-black text-[#4A5568]/50 uppercase tracking-[0.3em] leading-none">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default TestimonialsCarousel;